/**
 * @fileoverview A composable function for managing the platform.text.com chat widget.
 * This module provides functionality to initialize, manage, and interact with the LiveChat widget.
 *
 * @module useLivechat
 */

/**
 * A composable function that manages the LiveChat widget lifecycle and interactions.
 * Provides methods to initialize, clear, and handle messages for the chat widget.
 *
 * @returns {Object} An object containing public methods to interact with the LiveChat widget
 * @returns {Function} returns.init - Initializes the LiveChat widget
 * @returns {Function} returns.clear - Clears the widget state and destroys the instance
 * @returns {Function} returns.onMessage - Handles incoming message events
 */
export const useLivechat = () => {
    /**
     * Internal state object to manage widget initialization status
     * @private
     * @type {Object}
     * @property {boolean} initialized - Flag indicating if the widget is initialized
     * @property {number|null} timeout - Timer reference for reinitialization
     * @property {boolean} ready - Flag indicate LiveChatWidget has trigger "ready"
     * @property {number} reInitTimeout - Delay in milliseconds before reinitializing
     * @property {number} iteration - number of initialization performed
     * @property {number} stopAt - number of initialization failure until stop re-initializing
     */
    const state = {
        initDelay: 0, // a initial timeout BEFORE the widget to init.
    };

    /**
     * Creates and initializes the LiveChat widget with specified configuration
     * @private
     * @param {string} groupId - The unique identifier for the chat channel
     */
    const _createWidget = (groupId) => {
        // @link https://platform.text.com/docs/extending-chat-widget/javascript-api
        window.__lc = window.__lc || {};
        window.__lc.license = import.meta.env.VITE_LIVECHAT_LICENCE;
        window.__lc.group = groupId;
        // window.__lc.chat_between_groups = false;
        // window.__lc.asyncInit = true;
        window.__lc.integration_name = "manual_channels";
        window.__lc.product_name = "livechat";

        (function (n, t, c) {
            function i(n) {
                return e._h ? e._h.apply(null, n) : e._q.push(n);
            }
            const e = {
                _q: [],
                _h: null,
                _v: "2.0",
                on: function () {
                    i(["on", c.call(arguments)]);
                },
                once: function () {
                    i(["once", c.call(arguments)]);
                },
                off: function () {
                    i(["off", c.call(arguments)]);
                },
                get: function () {
                    if (!e._h)
                        throw new Error(
                            "[LiveChatWidget] You can't use getters before load.",
                        );
                    return i(["get", c.call(arguments)]);
                },
                call: function () {
                    i(["call", c.call(arguments)]);
                },
                init: function () {
                    const n = t.createElement("script");
                    n.async = true;
                    n.type = "text/javascript";
                    n.src = "https://cdn.livechatinc.com/tracking.js";
                    t.head.appendChild(n);
                },
            };
            (!n.__lc.asyncInit && e.init(),
                (n.LiveChatWidget = n.LiveChatWidget || e));
        })(window, document, [].slice);
    };
    /**
     * Updates the LiveChat widget with user context variables
     * Passes user information, brand details, and game data to the chat session
     * @private
     */
    const _updateLivechatVariables = () => {
        const sessionVariables = {};

        if (window.appContext.Player) {
            LiveChatWidget.call(
                "set_customer_name",
                window.appContext.Player.name,
            );
            LiveChatWidget.call(
                "set_customer_email",
                window.appContext.Player.email,
            );

            sessionVariables.ID = window.appContext.Player.uuid;
            sessionVariables.Phone = window.appContext.Player.phone;
            sessionVariables.Balance = window.appContext.Player.balance;
            sessionVariables.Currency = window.appContext.Player.currencyCode;
            sessionVariables.Tagged = window.appContext.Player.isTagged
                ? "Yes"
                : "No";
            sessionVariables.VIP = window.appContext.Player.vipStatus;
        }

        if (window.appContext.Brand) {
            sessionVariables.Brand = window.appContext.Brand.name;
        }

        // Add Affiliate Source if provided in appContext
        const affiliateSource =
            window.appContext?.Config?.livechat?.affiliateSource;
        if (affiliateSource) {
            sessionVariables.AffiliateSource = affiliateSource;
        }

        if (
            window.appContext.Game &&
            Object.keys(window.appContext.Game).length > 0
        ) {
            const allowedProperties = {
                name: "Game",
                type: "Type",
                provider: "Provider",
                eventID: "Event",
            };

            for (const [attribute, variableName] of Object.entries(
                allowedProperties,
            )) {
                const value = window.appContext.Game[attribute];
                if (!!value) {
                    sessionVariables[variableName] = value;
                }
            }
        }

        // Add Brand emails if available
        if (window.appContext.Brand?.emails) {
            const emails = window.appContext.Brand.emails;

            sessionVariables["Support email"] = emails.support;
            sessionVariables["Vip email"] = emails.vip;
            sessionVariables["Complaints email"] = emails.complaints;
            sessionVariables["Kyc email"] = emails.kyc;
            sessionVariables["Self exclusion email"] = emails.selfExclusion;
        }

        LiveChatWidget.call("set_session_variables", sessionVariables);
    };
    /**
     * Event handler for the widget's ready event
     * Sets customer information in the chat session
     * @private
     * @param {Object} event - The ready event object
     * @param {Object} event.data - The event data containing customer information
     * @param {string} event.data.customer_name - Customer's name
     * @param {string} event.data.customer_email - Customer's email
     * @param {Object} event.data.session_variables - Additional session variables
     */
    const _onReady = (event) => {
        /**
         * Event data is not trustable. It might miss the customer_name or such.
         * For example on 404/500 pages or if the user is ban or simply not logged in.
         * The widget is ready but without any customer data.
         */

        if (event.data?.customer_name) {
            LiveChatWidget.call("set_customer_name", event.data.customer_name);
        }
        if (event.data?.customer_email) {
            LiveChatWidget.call(
                "set_customer_email",
                event.data.customer_email,
            );
        }
        if (event.data?.session_variables) {
            LiveChatWidget.call(
                "set_session_variables",
                event.data.session_variables,
            );
        }
    };
    /**
     * Enables all chat-related links in the document
     * Removes the 'live-chat-disabled' class from elements
     * @private
     */
    const _enable_links = () => {
        Array.from(
            document.getElementsByClassName("live-chat-disabled"),
        ).forEach((e) => {
            e.classList.remove("live-chat-disabled");
            e.addEventListener("click", function (evt) {
                evt.preventDefault();
                const { visibility } = LiveChatWidget.get("state");
                if (visibility !== "maximized") {
                    LiveChatWidget.call("maximize");
                }
            });
        });
    };

    /**
     * Handles visibility changes of the LiveChat widget
     * Updates the visibility state in the app context and Alpine store
     * @private
     * @param {Object} data - The visibility change event data
     * @param {string} data.visibility - The current visibility state ("maximized", "minimized", or "hidden")
     */
    const _onVisibilityChanged = (data) => {
        if (window.Alpine?.store("livechat")) {
            window.Alpine.store("livechat").visibility = data.visibility;
        }
    };

    /**
     * Initializes the chat widget once it's ready
     * Enables chat links and updates LiveChat variables
     * @private
     */
    const _on_widget_ready = () => {
        _enable_links();
        _updateLivechatVariables();
    };

    /**
     * Handles the internal initialization sequence of the LiveChat widget
     * @private
     */
    const _bootstrap = () => {
        LiveChatWidget.init();
        state.initialized = true;
        LiveChatWidget.on("ready", _on_widget_ready);
        LiveChatWidget.on("visibility_changed", _onVisibilityChanged);
    };

    /**
     * A function to get the current user's language.
     * In a real application, this would detect the language from browser settings,
     * user profile, URL, or HTML lang attribute.
     * @returns {string} The current language code (e.g., "EN", "FR").
     */
    const _get_current_language = () => {
        const store = window.Alpine.store("locale");
        return store?.short.toUpperCase() ?? "EN";
    };
    /**
     * Resolves the LiveChat group ID based on the configuration.
     * The configuration can be a direct ID (number or numeric string)
     * or a language-keyed string (e.g., "EN:17,FR:18").
     *
     * @param {string|number} configValue - The configuration value for the group ID.
     * @returns {string|number|undefined} The resolved group ID, or undefined if resolution fails.
     */
    const _get_group_id = (configValue) => {
        if (configValue === null || typeof configValue === "undefined") {
            console.error(
                "[LiveChatWidget] Group ID configuration is null or undefined.",
            );
            return undefined;
        }

        // Case 1: It's a number
        if (typeof configValue === "number") {
            return configValue;
        }

        // Case 2: It's a string
        if (typeof configValue === "string") {
            const trimmedConfig = configValue.trim();

            // Subcase 2a: Empty string should be treated as "not configured"
            if (trimmedConfig.length === 0) {
                console.info(
                    "[LiveChatWidget] Group ID configuration is empty. Skipping LiveChat initialization.",
                );
                return undefined;
            }

            // Subcase 2b: It's a numeric string (e.g., "17")
            // Check if it's purely digits. !isNaN allows things like "  17  " or even "0xFF"
            // /^\d+$/ is stricter for positive integers.
            if (/^\d+$/.test(trimmedConfig)) {
                return trimmedConfig; // Return as string, or Number(trimmedConfig) if number type is preferred
            }

            // Subcase 2c: It's a language sequence (e.g., "EN:17,FR:18")
            // A simple check for ':' is a good indicator.
            if (trimmedConfig.includes(":")) {
                const languageMap = new Map();
                const pairs = trimmedConfig.split(",");

                for (const pair of pairs) {
                    const parts = pair.split(":");
                    if (parts.length === 2) {
                        const lang = parts[0].trim().toUpperCase();
                        const id = parts[1].trim();
                        if (lang && id) {
                            // Ensure both parts are non-empty
                            languageMap.set(lang, id);
                        } else {
                            console.warn(
                                `[LiveChatWidget] Skipping invalid pair in group ID sequence: "${pair}" from "${trimmedConfig}"`,
                            );
                        }
                    } else {
                        console.warn(
                            `[LiveChatWidget] Skipping malformed pair in group ID sequence: "${pair}" from "${trimmedConfig}"`,
                        );
                    }
                }

                if (languageMap.size === 0) {
                    console.error(
                        `[LiveChatWidget] Could not parse any valid language-ID pairs from sequence: "${trimmedConfig}"`,
                    );
                    return undefined;
                }

                const currentLang = _get_current_language();

                if (languageMap.has(currentLang)) {
                    return languageMap.get(currentLang);
                } else if (languageMap.has("EN")) {
                    console.warn(
                        `[LiveChatWidget] Current language '${currentLang}' not found in group ID config. Falling back to 'EN'.`,
                    );
                    return languageMap.get("EN");
                } else {
                    console.error(
                        `[LiveChatWidget] Default language 'EN' not found in the group ID sequence: "${trimmedConfig}", and current language '${currentLang}' is also not present.`,
                    );
                    return undefined;
                }
            }

            // If it's a string, but not numeric and not a valid sequence, it's an error.
            console.error(
                `[LiveChatWidget] Invalid group ID string format: "${configValue}". Expected a numeric string or a language sequence like "EN:1,FR:2".`,
            );
            return undefined;
        }

        // If configValue is neither a number nor a string (e.g. boolean, object)
        console.error(
            `[LiveChatWidget] Unexpected type for group ID configuration: ${typeof configValue}. Value: ${configValue}`,
        );
        return undefined;
    };

    /**
     * Initializes the LiveChat widget
     * Controls the widget lifecycle, including creation and initialization
     * @returns {void}
     */
    const init = () => {
        setTimeout(() => {
            // Always get default group ID as fallback
            let groupId = _get_group_id(
                import.meta.env.VITE_LIVECHAT_DEFAULT_GROUP_ID,
            );
            // Empty vip groupId by default
            let vipGroupId;

            if (window.appContext.Player?.vipInfo?.isVip) {
                vipGroupId = _get_group_id(
                    import.meta.env.VITE_LIVECHAT_VIP_GROUP_ID,
                );
            }
            // Priority: VIP group ID first, then default (order matters with || operator)
            _createWidget(vipGroupId || groupId);
            _bootstrap();
        }, state.initDelay);
    };

    /**
     * Handles incoming message events for the LiveChat widget
     * Processes messages from the same origin with liveChatData name
     * @public
     * @param {MessageEvent} event - The message event object
     * @param {string} event.origin - Origin of the message
     * @param {Object} event.data - Message data
     * @param {string} event.data.name - Name of the message event
     */
    const onMessage = (event) => {
        if (
            window.location.origin === event.origin &&
            !!event.data.name &&
            event.data.name === "liveChatData" &&
            typeof LiveChatWidget !== "undefined"
        ) {
            LiveChatWidget.on("ready", _onReady);
        }
    };

    return {
        init,
        onMessage,
    };
};
